import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SEO from '../../components/SEO'
import FAQSection from '../../components/FAQSection'

export default function BwindiNatureWalks() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    {
      question: "Which trail should I choose?",
      answer: "It depends on your fitness level and interests! Munyaga Waterfall Trail (3-4 hours, easy) is perfect for most visitors, families, and first-timers—you'll see waterfalls, primates, and butterflies. Rushura Hill (3-4 hours, moderate) is ideal if you want panoramic views of the Virungas and Rwenzoris. For serious birdwatchers, try Mubwindi Swamp Trail (4-5 hours) in Ruhija. Ivy River Trail (6-8 hours, challenging) is for experienced hikers wanting a full forest immersion. We'll help you choose based on your interests and physical condition."
    },
    {
      question: "Will I see gorillas on nature walks?",
      answer: "No. Nature walks use completely different trails from gorilla trekking routes to avoid disturbing the habituated families. However, you'll likely see other primates—black-and-white colobus monkeys, L'Hoest's monkeys, blue monkeys, red-tailed monkeys, and olive baboons are commonly spotted. Some trails also pass through chimpanzee territory, though chimps are less habituated and sightings aren't guaranteed."
    },
    {
      question: "Can children participate?",
      answer: "Yes! Children aged 8 and above can join nature walks, making this a fantastic family alternative to gorilla trekking (which requires participants to be 15+). The easier trails like Munyaga Waterfall are very suitable for kids who enjoy nature. Just ensure they're comfortable walking for 2-4 hours on uneven terrain."
    },
    {
      question: "How difficult are the trails physically?",
      answer: "Trail difficulty varies significantly. Easy trails like Munyaga Waterfall involve gentle slopes on established paths—most people with basic fitness can manage. Moderate trails like Rushura Hill and Mubwindi Swamp include steeper sections and require reasonable fitness. Challenging trails like Ivy River and Bamboo Trail involve 6-8 hours of hiking over steep, muddy terrain with elevation changes—these require good fitness levels. All trails can be slippery when wet (which is often in Bwindi!)."
    },
    {
      question: "Can I swim at the waterfalls?",
      answer: "Yes! Swimming is permitted at Munyaga Falls. The water is quite cold (expect around 15-18°C/59-64°F) but incredibly refreshing after your hike. Bring a swimsuit if you'd like to take a dip. The natural pools are beautiful and make for great photo opportunities even if you don't swim."
    },
    {
      question: "What time of year is best for nature walks?",
      answer: "Bwindi's trails are accessible year-round, but the dry seasons (June-September and December-February) offer easier hiking conditions with less mud and fewer leeches. That said, the \"dry\" season in Bwindi is relative—rain can occur any day. The wet seasons (March-May, October-November) mean muddier trails but also lush vegetation, more active waterfalls, and excellent butterfly viewing. Serious birdwatchers often prefer November-April when migratory species are present."
    },
    {
      question: "Do I need a guide?",
      answer: "Yes, all nature walks in Bwindi National Park require a UWA (Uganda Wildlife Authority) ranger guide for safety and conservation purposes. Your guide is included in the permit fee. They're knowledgeable about the forest ecology, medicinal plants, wildlife behaviors, andwill significantly enhance your experience with expert interpretation."
    },
    {
      question: "Can I combine nature walks with gorilla trekking?",
      answer: "Absolutely! Many visitors do a gorilla trek one day and a nature walk another day during their Bwindi visit. If you're doing a gorilla trek the same day, the park entrance fee from your gorilla permit covers nature walks too. Otherwise, you'll need a separate park entrance fee plus the nature walk permit. We can help you design a multi-day itinerary combining both activities."
    }
  ];

  return (
    <div>
      <SEO
        title="Bwindi Forest Nature Walks – Waterfall Trails & Primates | 2024/2025"
        description="Explore Bwindi's ancient rainforest on guided nature walks. Discover Munyaga Waterfall, spot colobus monkeys, learn medicinal plants, and enjoy family-friendly trails (8+). 6+ trails from easy 3-hour walks to challenging 8-hour treks across Buhoma, Ruhija sectors. Book your Uganda forest adventure."
      />
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Bwindi Forest Nature Walks – 2024/2025
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Explore waterfalls, primates, and medicinal plants in one of Africa's oldest rainforests
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(180+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-droplet" className="w-4 h-4 mr-2" />
                Waterfall Trails
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                UNESCO Heritage Site
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Family Friendly (8+ Years)
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Nature Walk
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Beyond Gorillas: Discover Bwindi's Hidden Treasures
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  While Bwindi Impenetrable Forest is globally famous for mountain gorillas, the forest itself deserves equal attention as one of Africa's most remarkable ecological treasures. Dating back over 25,000 years, Bwindi survived the last ice age as a rainforest refuge, making it one of the continent's most ancient and biodiverse ecosystems. This UNESCO World Heritage Site spans 331 square kilometers of montane and lowland forest across elevations from 1,160 to 2,706 meters.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Nature walks through Bwindi reveal layers of complexity that gorilla trekking's singular focus doesn't fully capture. You'll discover cascading waterfalls tumbling through moss-covered ravines, troops of black-and-white colobus monkeys and L'Hoest's monkeys leaping through the canopy, medicinal plants that traditional healers have used for millennia, and an understory alive with over 200 butterfly species, chameleons, and duikers. The park protects over 120 mammal species, 350 bird species (including 24 Albertine Rift endemics), and 400+ types of plants.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your UWA ranger guide won't just lead the way—they'll transform your hike into an immersive educational experience. As you walk, they'll point out medicinal plants like the wild ginger used for stomach ailments and tree ferns with antiseptic properties. You'll learn to identify tree species by bark texture and leaf shape, spot fresh elephant dung (forest elephants occasionally pass through), and understand how the forest's layered canopy creates distinct microclimates. It's a gentler, more contemplative way to experience Bwindi, perfect for nature enthusiasts, families with children 8+, and those seeking a deeper forest connection.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Why Choose Nature Walks?</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Nature walks offer several advantages: they're significantly more affordable than gorilla permits, suitable for younger children (8+ vs. 15+ for gorilla trekking), less physically demanding with customizable difficulty levels, and provide broader wildlife diversity. You're more likely to see multiple primate species, birds, and butterflies on a nature walk than on a focused gorilla trek. Plus, trails operate across all four sectors—Buhoma, Ruhija, Nkuringo, and Rushaga—giving you flexibility in your itinerary.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                What Makes These Walks Special
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-droplet', title: 'Spectacular Waterfalls', description: 'Hike to the 33-meter Munyaga Falls with opportunities for photos and refreshing swims in natural pools' },
                  { icon: 'fa-eye', title: 'Frequent Primate Sightings', description: 'Black-and-white colobus, L\'Hoest\'s monkeys, blue monkeys, red-tailed monkeys, and olive baboons commonly encountered' },
                  { icon: 'fa-leaf', title: 'Medicinal Plant Education', description: 'Learn how traditional healers use wild ginger, tree ferns, and other forest plants from expert ranger guides' },
                  { icon: 'fa-camera', title: 'Photography Paradise', description: 'Capture cascading waterfalls, 200+ butterfly species, forest hornbills, turacos, and misty forest landscapes' },
                  { icon: 'fa-binoculars', title: 'Excellent Birdwatching', description: 'Spot Albertine Rift endemics like African green broadbill, turacos, hornbills, and sunbirds' },
                  { icon: 'fa-users', title: 'Family-Friendly Options', description: 'Children 8+ welcome (vs. 15+ for gorillas), with trails ranging from easy 2-hour walks to challenging 7-hour treks' },
                  { icon: 'fa-mountain', title: 'Panoramic Viewpoints', description: 'Rushura Hill offers stunning views of Virunga Volcanoes, Lake Edward, and Rwenzori Mountains on clear days' },
                  { icon: 'fa-route', title: 'Diverse Trail Options', description: 'Choose from 6+ trails across four sectors: easy riverside strolls to multi-sector forest traverses' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Trail Options */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Choose Your Trail Adventure
              </h2>
              <p className="text-center text-gray-700 mb-12 text-lg">
                Bwindi offers diverse trails across all four sectors. Here are the most popular options:
              </p>

              <div className="space-y-6">
                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-safari-green">Munyaga Waterfall Trail</h3>
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">EASY • 3-4 Hours</span>
                    </div>
                    <p className="text-gray-700 mb-3"><strong>Location:</strong> Buhoma Sector</p>
                    <p className="text-gray-700 mb-3">
                      The most popular trail, following the Bizenga Creek to where it meets the Munyaga River. You'll hike through lowland rainforest to a spectacular 33-meter waterfall with opportunities for swimming in natural pools. Excellent for spotting black-and-white colobus monkeys, L'Hoest's monkeys, colorful butterflies, and forest birds including turacos and hornbills. The trail includes medicinal plant interpretation and is perfect for families and first-time visitors.
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-safari-green">Rushura Hill Trail</h3>
                      <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">MODERATE • 3-4 Hours</span>
                    </div>
                    <p className="text-gray-700 mb-3"><strong>Location:</strong> Buhoma Sector</p>
                    <p className="text-gray-700 mb-3">
                      Ascend to 2,500 meters for breathtaking panoramic views of Lake George, Lake Edward, the Rwenzori Mountains (on clear days), and the conical peaks of the Virunga volcanoes. This moderate climb rewards you with stunning vistas and crosses forests spanning both Uganda and the Democratic Republic of Congo. Watch for bushbucks, duikers, giant forest hogs, and forest elephants along the way.
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-safari-green">Ivy River Trail</h3>
                      <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">CHALLENGING • 6-8 Hours</span>
                    </div>
                    <p className="text-gray-700 mb-3"><strong>Location:</strong> Connects Buhoma and Nkuringo Sectors (13km)</p>
                    <p className="text-gray-700 mb-3">
                      One of the oldest and most immersive hikes in Bwindi, this full-day trek follows the Ivy River through thick vegetation with river crossings over wooden bridges and stepping stones. You'll pass through ancient forest with trees over 2,500 years old, interact with local Batwa pygmy communities, and have excellent chances of seeing red-tailed monkeys, chimpanzees, elephants, giant forest hogs, duikers, and olive baboons. A paradise for serious birdwatchers seeking Albertine Rift endemics.
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-safari-green">Mubwindi Swamp Trail</h3>
                      <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">MODERATE • 4-5 Hours</span>
                    </div>
                    <p className="text-gray-700 mb-3"><strong>Location:</strong> Ruhija Sector</p>
                    <p className="text-gray-700 mb-3">
                      Winds through rainforest to the Mubwindi Swamp, known as one of Bwindi's premier birding trails. Offers scenic viewpoints of the Virunga ranges and excellent opportunities to spot various primates (including occasional chimpanzee sightings), forest elephants, bush pigs, and numerous bird species. The swamp ecosystem attracts unique waterbirds and insects.
                    </p>
                  </div>
                </div>

                <div className="card">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-2xl font-bold text-safari-green">Bamboo Trail</h3>
                      <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">STRENUOUS • 6-7 Hours</span>
                    </div>
                    <p className="text-gray-700 mb-3"><strong>Location:</strong> Ruhija Sector</p>
                    <p className="text-gray-700 mb-3">
                      The park's most challenging nature walk, ascending through different vegetation zones to Rwamunyonyi Hill (2,607m)—Bwindi's highest point. Pass through afro-montane and bamboo forests with spectacular views of Lake Bunyonyi, the Virunga Volcanoes, and Bwindi's vast canopy. Excellent for serious hikers seeking diverse wildlife including duikers, forest elephants, multiple primate species, and rare high-altitude birds.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Sample Day */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Day in Detail – Munyaga Waterfall Trail
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences,
                  which trails you'd like to explore, and your fitness level. We can adjust timing and combine multiple activities
                  to suit your interests.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '09:00', title: 'Trail Briefing', desc: 'Meet your UWA ranger guide at Buhoma Park Headquarters for registration and orientation. They\'ll explain trail safety, wildlife viewing etiquette, and what to expect.' },
                  { time: '09:15', title: 'Forest Entry', desc: 'Begin your gentle walk along the Bizenga Creek through lowland rainforest. Your guide points out medicinal plants, explains their traditional uses, and helps you identify bird calls echoing through the canopy.' },
                  { time: '09:45', title: 'Primate Territory', desc: 'Pass through areas frequented by black-and-white colobus monkeys and L\'Hoest\'s monkeys. Watch them leap between branches as your guide explains their social behaviors and feeding habits.' },
                  { time: '10:30', title: 'Waterfall Arrival', desc: 'Reach the spectacular 33-meter Munyaga Falls cascading into natural pools. Time for photography, snacks, and optional swimming (water is cold but refreshing!). Your guide shares the ecological importance of the waterfall system.' },
                  { time: '11:15', title: 'Return Journey', desc: 'Hike back via a different route, focusing on butterfly identification and smaller forest details—chameleons, forest floor insects, and unique orchids. Your guide might spot fresh duiker or elephant tracks.' },
                  { time: '12:00', title: 'Trail End', desc: 'Exit the forest and review your sightings with your guide. Receive your trail completion certificate and head back to your lodge for lunch.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-xl">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring on Your Nature Walk
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof hiking boots (ankle support recommended)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Rain jacket or poncho (essential—Bwindi is very wet)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Long pants (tucked into socks to prevent insect bites)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Long-sleeved shirt (lightweight, breathable)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Wide-brimmed hat and sunglasses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Garden gloves (helpful for gripping wet branches)</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-bag-shopping" className="w-6 h-6" />
                    Essentials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>2+ liters of water (stay hydrated)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Energy snacks (nuts, dried fruit, chocolate)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Insect repellent (DEET-based recommended)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunscreen (SPF 30+)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Small first aid kit (bandages, pain relievers)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Personal medications</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                    Photography & Extras
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Camera with zoom lens (for primate photography)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Binoculars (for birdwatching)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Extra batteries and memory cards</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof bag/cover for electronics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Field guide to East African birds (optional)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Walking stick/hiking poles (can be rented)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Your Questions Answered" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Bwindi Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=800"
                      alt="Gorilla Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                    <p className="text-gray-700 mb-4">Meet mountain gorillas face-to-face in their natural habitat—the ultimate wildlife encounter.</p>
                    <p className="text-safari-gold font-bold">Permit fees apply – contact us for current rates</p>
                  </div>
                </Link>

                <Link to="/activities/batwa-cultural-trail" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800"
                      alt="Batwa Cultural Trail"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Batwa Cultural Trail</h3>
                    <p className="text-gray-700 mb-4">Walk with the forest's original inhabitants and learn ancient survival skills from the Batwa people.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us for pricing</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 2-8 hours (trail dependent)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Jun-Sep, Dec-Feb (year-round possible)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Permit Fee (2024-2026):</strong> $30 USD per person</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Easy to Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Age:</strong> 8+ years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Nature Walk
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "UWA ranger guide (expert naturalist)",
                    "Nature walk permit",
                    "Trail access to all designated routes",
                    "Ecological and medicinal plant interpretation",
                    "Trail safety briefing and escort",
                    "Trail completion certificate"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to/from Bwindi",
                    "Park entrance fee ($40 USD—waived if gorilla trekking same day)",
                    "Packed lunch or snacks",
                    "Hiking poles (available for rent locally)",
                    "Porter service (optional, supports local community)",
                    "Gratuities for guide",
                    "Accommodation before/after walk",
                    "Personal travel insurance"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness Level:</strong> Varies by trail—easy to strenuous options available.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Terrain:</strong> Muddy, slippery forest paths with elevation changes.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Altitude:</strong> 1,160-2,706 meters depending on trail.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Bwindi Forest Nature Walk"
      />
    </div>
  )
}
